import { MarketingFlagKey } from "@redotech/feature-flags-keys-marketing/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { useState } from "react";
import { ActionRequiredSeverity } from "./action-required-notification";

/**
 * Prompts consent for shopper first-name + age sharing whenever a provider is
 * connected and consent isn't recorded (retroactive; one team-level accept).
 */
export function useOfferNetworkDataSharing(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const [pending, setPending] = useState(false);

  const { data: offersFlag } = useRpcQuery(
    RedoPlatformAuthRpcClientContext,
    "getFeatureFlag",
    { flagKey: MarketingFlagKey.OFFERS_TRACKING_PAGE_BLOCK },
  );
  const { data: offerNetwork, refetch } = useRpcQuery(
    RedoMarketingRpcClientContext,
    "getOfferNetworkIntegrations",
    null,
    { enabled: !!offersFlag?.enabled },
  );

  if (
    !offerNetwork ||
    !offerNetwork.anyProviderConnected ||
    offerNetwork.dataSharingConsent
  ) {
    return null;
  }

  const item: NotificationBannerItem = {
    id: "offer-network-data-sharing",
    type: "info",
    message:
      "Customers' first name and age are shared with a third-party partner network to show the most relevant offerings.",
    actions: [
      {
        actionType: "button",
        hierarchy: "primary",
        text: "Accept",
        pending,
        onClick: () => {
          setPending(true);
          void marketingRpcClient
            .setOfferNetworkDataSharingConsent({ acknowledged: true })
            .then(() => refetch())
            .finally(() => setPending(false));
        },
      },
    ],
  };

  return { severity: ActionRequiredSeverity.Low, item };
}
